import isNumber from 'is-number';

import {WikiSearchArgs} from '~shared/schema/wiki/types';
import {WikiSearchData, WikiSearchDocumentType, WikiSearchOrderType} from '~shared/models/wiki';

import {entries, isEnum, isObject} from '~ui/utils';

import {SearchDateFilterType, SearchQueryParams} from './types';

export function parseSearchQuery(search: string, isMobile: boolean): SearchQueryParams {
    const params = new URLSearchParams(search);

    return {
        text: params.get('text') ?? '',
        // Мобильный поиск всегда должен начинаться с первой страницы
        // т.к. там infinite scroll
        page: isMobile ? 1 : getPage(params.get('page') ?? ''),
        order: getOrderBy(params.get('order') ?? ''),
        date: getDate(params),
        documentType: getDocumentType(params.get('documentType') ?? ''),
        showObsolete: getShowObsolete(params.get('showObsolete') ?? ''),
        slug: params.get('slug') || undefined,
        fuzzy: isMobile,
    };
}

const getPage = (str: string) => {
    const page = isNumber(str) ? parseInt(str) : 1;

    if (page < 1) {
        return 1;
    }

    return page;
};

const getOrderBy = (str: string): WikiSearchOrderType => {
    return isEnum(str, WikiSearchOrderType) ? str : WikiSearchOrderType.Relevancy;
};

const getDate = (params: URLSearchParams) => {
    const dateFrom = params.get('dateFrom') ?? '';
    const dateTo = params.get('dateTo') ?? '';
    const dateType = params.get('dateType') ?? '';
    if (!dateFrom && !dateTo && !dateType) return;

    const parsedDateFrom = isNumber(dateFrom) ? parseInt(dateFrom) : undefined;
    const parsedDateTo = isNumber(dateTo) ? parseInt(dateTo) : undefined;
    const parsedDateType = isEnum(dateType, SearchDateFilterType)
        ? dateType
        : SearchDateFilterType.MODIFICATION;

    if (!parsedDateFrom && !parsedDateTo && !parsedDateType) return;

    return {
        from: parsedDateFrom,
        to: parsedDateTo,
        type: parsedDateType,
    };
};

const getDocumentType = (value: string) => {
    return isEnum(value, WikiSearchDocumentType) ? value : WikiSearchDocumentType.ALL;
};

const getShowObsolete = (value: string) => {
    if (value === 'true') return true;
    return false;
};

export function toSearchArgs({
    page,
    text,
    order,
    date,
    documentType,
    authors,
    showObsolete,
    slug,
    fuzzy,
}: SearchQueryParams): WikiSearchArgs {
    const type =
        documentType && documentType !== WikiSearchDocumentType.ALL ? documentType : undefined;

    const {from: dateFrom, to: dateTo, type: dateType} = date || {};

    const filterDate =
        dateFrom || dateTo
            ? {
                  from: dateFrom ? Math.round(dateFrom / 1000) : undefined,
                  to: dateTo ? Math.round(dateTo / 1000) : undefined,
              }
            : undefined;

    const dateFilterName =
        dateType === SearchDateFilterType.CREATION ? 'created_at' : 'modified_at';

    const authorCloudIds = authors?.reduce<string[]>((acc, {cloudId}) => {
        if (cloudId) {
            acc.push(cloudId);
        }
        return acc;
    }, []);

    const authorCloudIdsFilter =
        !authorCloudIds || authorCloudIds.length === 0
            ? undefined
            : authorCloudIds.map((id) => ({
                  cloud_uid: id,
              }));
    const slugFilter = slug ? trimSlashes(slug) : undefined;

    return {
        query: text,
        page_id: page,
        page_size: 10,
        order_by: order,
        filters: {
            type,
            [dateFilterName]: filterDate,
            authors: authorCloudIdsFilter,
            show_obsolete: showObsolete,
            cluster: slugFilter,
        },
        highlight: !fuzzy,
    };
}

const trimSlashes = (value: string) => {
    let result = value;
    if (result.startsWith('/')) {
        result = result.slice(1);
    }
    if (result.endsWith('/')) {
        result = result.slice(0, result.length - 1);
    }
    return result;
};

export function updateLocation(params: SearchQueryParams) {
    const href = new URL(window.location.href);
    const {searchParams} = href;

    const updateParam = (param: string, value: unknown) => {
        value ? searchParams.set(param, String(value)) : searchParams.delete(param);
    };

    for (const entry of entries(params)) {
        switch (entry[0]) {
            case 'authors': {
                const value = entry[1];
                updateParam('authors', value?.map((v) => v.cloudId).toString());
                break;
            }
            case 'date': {
                const value = entry[1];
                const {from: dateFrom, to: dateTo, type: dateType} = value || {};
                updateParam('dateFrom', dateFrom);
                updateParam('dateTo', dateTo);
                updateParam('dateType', dateType);
                break;
            }
            default:
                updateParam(entry[0], entry[1]);
        }
    }

    window.history.replaceState(null, '', href.toString());
}

export function adaptFetchResultsResponse(data: unknown): WikiSearchData {
    const wikiData = isObject(data) ? data : {};
    const {page_id, results, total_documents, total_pages} = wikiData;

    return {
        page_id: page_id || 1,
        results: results || [],
        total_documents: total_documents || 0,
        total_pages: total_pages || 0,
    };
}

export function hasDateRange(date: SearchQueryParams['date']) {
    if (!date) return false;
    if (!date.from && !date.to) return false;
    return true;
}

export const getMetrikaSearchOrder = (order: WikiSearchOrderType) => {
    switch (order) {
        case WikiSearchOrderType.CreationDate:
            return 'Дата_Создания';
        case WikiSearchOrderType.ModifiedDate:
            return 'Дата_Изменения';
        case WikiSearchOrderType.Relevancy:
            return 'Релевантность';
    }
};

export const getMetrikaDateType = (type: SearchDateFilterType) => {
    switch (type) {
        case SearchDateFilterType.CREATION:
            return 'Дата_Создания';
        case SearchDateFilterType.MODIFICATION:
            return 'Дата_Изменения';
        default:
            return null;
    }
};

export const getMetrikaDocumentType = (type: WikiSearchDocumentType | undefined) => {
    switch (type) {
        case WikiSearchDocumentType.GRID:
            return 'Таблица';
        case WikiSearchDocumentType.PAGE:
            return 'Страница';
        case WikiSearchDocumentType.FILE:
            return 'Файл';
        case WikiSearchDocumentType.ALL:
        default:
            return 'Все';
    }
};
